import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  FRECUENCIAS_PAGO,
  finDeMes,
  formatMoney,
  formatQuantity,
  hoy,
  newId,
  parseMoney,
  rangoDeQuincena,
  rangoDeSemana,
  rangoDelMes,
  seCruzan,
  type Centavos,
  type CuentaBancoCordobas,
  type FrecuenciaPago,
  type Milli,
  type Rango,
  type Settings,
} from '@abastece/shared';
import { diasPagados, pagoDe, rangoDeCorrida, sueldoDeCorrida, type Empleado, type Planilla, type PlanillaLinea } from '@abastece/core';
import { db } from '../db/dexie.js';
import { cuentaEnCordobas, deDondeSalio } from '../lib/bancos.js';
import { emitirYVer } from '../lib/emitir.js';
import {
  SALARIO_DE,
  adelantoAlDia,
  adelantosDados,
  antesDeLaCorrida,
  borradorDeLinea,
  borradorVacio,
  corridasAntesDe,
  costoDeCorrida,
  dichoAcordado,
  lineaDeCorrida,
  muestraVacaciones,
  pagaPrestaciones,
  pagoDeLaCorrida,
  rangoSugerido,
  sueldosEnGastos,
  tituloDe,
  vacacionesApuntadas,
  tituloDeCorrida,
  type Borrador,
} from '../lib/planilla.js';
import { leerSinEfecto, tuvoEfecto } from '../lib/sinEfecto.js';
import { useT } from '../i18n/index.js';
import { Campo, Interruptor, Selector } from './Campo.js';
import { DesglosePlanilla } from './DesglosePlanilla.js';
import { ElegirCuenta } from './ElegirCuenta.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/**
 * Running the payroll: a sheet over «Planilla».
 *
 * A run pays the people of one schedule for one stretch of days (2026-10-12): a week that ends on
 * the day it says, the first or the second half of a month, a month. It starts where the last run
 * of that schedule left off. Somebody another run already paid for a day of it is shown and left
 * out — nobody is paid twice for the same day, which the books refuse anyway — and somebody paid
 * by the day has the days they worked typed here, half days too. What each line comes to is the
 * books' own arithmetic (`lib/planilla.ts`).
 *
 * Days already paid (the owner, 2026-10-31: «can we also have a way to log payments made in past pay
 * periods?»): «Ya se pagó» records the run and its payment together, on the day it was paid and the
 * way it was, a past stretch of days or this one. «Registrar un pago anterior» opens it that way.
 *
 * A run set right (the owner, 2026-10-31: «we need a way to be able to edit payroll runs from the runs
 * tab for corrections»): «Corregir» on a run opens it here, its days fixed, everybody on it with
 * their line as it booked, to change, to take off or to add somebody of its schedule; with nobody
 * left, it is voided. Each person is worked out as they were before it (`antesDeLaCorrida`), and
 * paid, its payment changes with it (`planilla.corregida`).
 */
export function CorrerPlanilla({
  activos,
  empleados = activos,
  corridas,
  config,
  pagadaAlAbrir = false,
  corrigiendo,
  onCerrar,
}: {
  activos: readonly Empleado[];
  /** Everybody, those settled too: somebody on a run being corrected may have left since. */
  empleados?: readonly Empleado[];
  corridas: readonly Planilla[];
  config: Settings;
  /** Opened to write down days already paid: «Ya se pagó» on. */
  pagadaAlAbrir?: boolean;
  /** The run being set right, read once as it was opened. */
  corrigiendo?: Planilla;
  /** Closed: recorded, or not. A run recorded whose payment the books refused says which, and why. */
  onCerrar: (pagoPendiente?: { planillaId: string; motivo: string }) => void;
}) {
  const t = useT();
  // The schedules somebody is on, in their order: one run is for one of them.
  const enUso = FRECUENCIAS_PAGO.filter((f) => activos.some((e) => pagoDe(e).frecuencia === f));
  const [frecuencia, setFrecuencia] = useState<FrecuenciaPago>(corrigiendo ? (corrigiendo.frecuencia ?? 'mensual') : (enUso[0] ?? 'mensual'));
  const [rango, setRango] = useState<Rango>(() => (corrigiendo ? rangoDeCorrida(corrigiendo) : rangoSugerido(enUso[0] ?? 'mensual', corridas, hoy())));

  // --- corrigiendo una corrida ---
  /** What the run being corrected booked for each person on it. */
  const [suLinea] = useState(() => new Map<string, PlanillaLinea>((corrigiendo?.lineas ?? []).map((l) => [l.empleadoId, l])));
  // Those on it first, as it had them; then those of its schedule it left out, to add.
  const enLaCorrida = corrigiendo ? corrigiendo.lineas.flatMap((l) => empleados.filter((e) => e.id === l.empleadoId)) : [];
  const liquidados = enLaCorrida.filter((e) => !e.activo).map((e) => e.nombre);
  // Somebody on it settled since: it is not corrected, and the sheet says only why.
  const bloqueada = liquidados.length > 0;
  const [nota, setNota] = useState('');
  // Paid, the payment the correction changes with it.
  const suPago = useLiveQuery(
    async () => {
      if (!corrigiendo?.pagada) return null;
      const [eventos, sinEfecto] = await Promise.all([db.eventos.where('type').equals('planilla.pagada').toArray(), leerSinEfecto()]);
      return pagoDeLaCorrida(corrigiendo, eventos, (e) => tuvoEfecto(e, sinEfecto));
    },
    [corrigiendo?.id],
    null,
  );

  const [borradores, setBorradores] = useState<Record<string, Borrador>>(() =>
    corrigiendo
      ? Object.fromEntries([
          ...corrigiendo.lineas.map((l) => [l.empleadoId, borradorDeLinea(l)] as const),
          ...activos.filter((e) => !suLinea.has(e.id)).map((e) => [e.id, { ...borradorVacio(e.id), incluir: false }] as const),
        ])
      : Object.fromEntries(activos.map((e) => [e.id, borradorVacio(e.id)])),
  );
  /** As it was opened: a correction that changes nothing is not saved. */
  const [alAbrir] = useState(borradores);
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  // --- ya se pagó ---
  const [yaSePago, setYaSePago] = useState(pagadaAlAbrir);
  /** The day it was paid, once typed; until then the stretch's last day, or today while it lasts. */
  const [fechaPagoEscrita, setFechaPagoEscrita] = useState<string | null>(null);
  const [formaPago, setFormaPago] = useState<'efectivo_cordobas' | 'transferencia' | 'billetera'>('efectivo_cordobas');
  /** The córdoba account a payment by transfer went out of, once picked. */
  const [cuentaPago, setCuentaPago] = useState<CuentaBancoCordobas | null>(null);
  const hoyEs = hoy();
  const fechaPago = fechaPagoEscrita ?? (rango.hasta < hoyEs ? rango.hasta : hoyEs);
  // Paid once the days had begun, and not after today.
  const fechaPagoBien = /^\d{4}-\d{2}-\d{2}$/.test(fechaPago) && fechaPago >= rango.desde && fechaPago <= hoyEs;

  // Salaries written down in «Gastos» that may be this pay: booked here as well, each would count twice.
  const gastosDeSueldos = useLiveQuery(() => db.gastos.where('categoria').equals('sueldos').toArray(), [], []);
  const enGastos = sueldosEnGastos(gastosDeSueldos, rango);
  // The advances the books took, whose and when: paid already, the run takes off only those given by then.
  const dados = useLiveQuery(
    async () => {
      const [eventos, sinEfecto] = await Promise.all([db.eventos.where('type').equals('empleado.adelanto').toArray(), leerSinEfecto()]);
      return adelantosDados(eventos, (e) => tuvoEfecto(e, sinEfecto));
    },
    [],
    [],
  );
  // Vacation days already written down outside the run (2026-10-31): said on the person's line.
  const apuntadas = useLiveQuery(
    async () => {
      const [eventos, sinEfecto] = await Promise.all([db.eventos.where('type').equals('empleado.vacaciones').toArray(), leerSinEfecto()]);
      return vacacionesApuntadas(eventos, (e) => tuvoEfecto(e, sinEfecto));
    },
    [],
    [],
  );

  // Nobody hired after the run's last day: there is nothing of it to pay them.
  const deSuFrecuencia = activos.filter((e) => pagoDe(e).frecuencia === frecuencia && e.fechaIngreso <= rango.hasta);
  const gente = corrigiendo ? [...enLaCorrida.filter((e) => e.activo), ...deSuFrecuencia.filter((e) => !suLinea.has(e.id))] : deSuFrecuencia;
  // A correction leaves the run itself out: it is what is being set right.
  const otras = corrigiendo ? corridas.filter((p) => p.id !== corrigiendo.id) : corridas;

  /** The run that already paid somebody for a day of these, if one did. */
  const yaPagada = (e: Empleado) =>
    otras.find((p) => !p.esLiquidacion && seCruzan(rangoDeCorrida(p), rango) && p.lineas.some((l) => l.empleadoId === e.id));

  /**
   * Somebody as the run works them out: as they are; or, on the run being corrected, as they were
   * before it, what it set aside, paid and took off them taken back.
   */
  const comoEstaba = (e: Empleado): Empleado => {
    const l = suLinea.get(e.id);
    return l ? antesDeLaCorrida(e, l, frecuencia, rango) : e;
  };
  /**
   * The most it takes off their advances: on a correction, what the run took off them, or for
   * somebody added to a paid run those given by the day it was paid; paid already, those given by then.
   */
  const adelantoMaximo = (e: Empleado): number | undefined => {
    if (corrigiendo) {
      const l = suLinea.get(e.id);
      if (l) return l.adelantoDescontado ?? 0;
      return corrigiendo.pagada && corrigiendo.pagadaEn ? adelantoAlDia(e, dados, corrigiendo.pagadaEn) : undefined;
    }
    return yaSePago && fechaPagoBien ? adelantoAlDia(e, dados, fechaPago) : undefined;
  };
  // The income tax goes on from the year's runs before these days; a correction's, from those before it.
  const delAnio = corrigiendo ? corridasAntesDe(corridas, corrigiendo) : corridas;

  const lineas = gente
    .filter((e) => borradores[e.id]?.incluir && !yaPagada(e))
    .map((e) => ({
      empleado: comoEstaba(e),
      linea: lineaDeCorrida(comoEstaba(e), borradores[e.id]!, frecuencia, rango, config, delAnio, adelantoMaximo(e)),
    }));
  const faltan = lineas.filter((x) => x.linea.falta === 'dias').map((x) => x.empleado.nombre);
  const ilegibles = lineas.filter((x) => x.linea.falta === 'ilegible').map((x) => x.empleado.nombre);
  const listos = lineas.flatMap((x) => (x.linea.falta ? [] : [{ empleado: x.empleado, linea: x.linea }]));
  const listas = listos.map((x) => x.linea);

  const previa = listas.reduce(
    (a, l) => ({
      neto: a.neto + l.calculo.neto,
      costo: a.costo + l.costo,
      adelantos: a.adelantos + l.adelanto,
    }),
    { neto: 0, costo: 0, adelantos: 0 },
  );

  function elegirFrecuencia(f: FrecuenciaPago) {
    setFrecuencia(f);
    setRango(rangoSugerido(f, corridas, hoy()));
    setError(null);
  }

  const cambio = JSON.stringify(borradores) !== JSON.stringify(alAbrir);
  const lista = corrigiendo
    ? cambio && liquidados.length === 0 && faltan.length === 0 && ilegibles.length === 0
    : listas.length > 0 && faltan.length === 0 && ilegibles.length === 0 && (!yaSePago || fechaPagoBien);
  // Nobody left on the run being corrected: it is voided.
  const anulando = corrigiendo !== undefined && listas.length === 0;
  // What a paid run handed over, and hands over corrected: the difference out of, or back into, what paid it.
  const entregado = corrigiendo ? corrigiendo.totalNeto - (corrigiendo.totalAdelantos ?? 0) : 0;
  const delPago = corrigiendo?.pagada ? previa.neto - previa.adelantos - entregado : 0;
  const pagadoCon = (() => {
    const dice = suPago?.payload as { formaPago?: string; cuenta?: string } | undefined;
    return dice ? deDondeSalio(config, dice.formaPago, dice.cuenta, t) : t('planilla.corregir.loQueLaPago');
  })();

  async function guardarCorreccion() {
    if (!corrigiendo || !lista) return;
    setError(null);
    setTrabajando(true);
    let rechazo: string | null;
    try {
      ({ rechazo } = await emitirYVer('planilla.corregida', {
        planillaId: corrigiendo.id,
        lineas: listas.map((l) => l.enviada),
        ...(suPago ? { pagoId: suPago.id } : {}),
        ...(nota.trim() !== '' ? { nota: nota.trim() } : {}),
      }));
    } catch (e) {
      rechazo = e instanceof Error ? e.message : String(e);
    }
    if (rechazo) {
      setError(rechazo);
      setTrabajando(false);
      return;
    }
    onCerrar();
  }

  async function correr() {
    if (!lista) return;
    setError(null);
    setTrabajando(true);
    const planillaId = newId();
    let rechazo: string | null;
    try {
      ({ rechazo } = await emitirYVer('planilla.corrida', {
        planillaId,
        periodo: rango.hasta.slice(0, 7),
        // What it costs goes in the days it pays: September's run, done on the 2nd of October, is
        // September's (2026-10-13). Done before its last day, the day it is done; paid before
        // either, the day it was paid, never owed after it was paid.
        fecha: [rango.hasta, hoyEs, ...(yaSePago ? [fechaPago] : [])].sort()[0]!,
        frecuencia,
        desde: rango.desde,
        hasta: rango.hasta,
        lineas: listas.map((l) => l.enviada),
      }));
    } catch (e) {
      // Not recorded at all — no device, no session, a line the books cannot read: said here.
      rechazo = e instanceof Error ? e.message : String(e);
    }
    if (rechazo) {
      setError(rechazo);
      setTrabajando(false);
      return;
    }
    if (!yaSePago) return onCerrar();

    // Paid already: the day it was, and how. Refused, the run stays recorded, waiting to be paid.
    let motivo: string | null;
    try {
      ({ rechazo: motivo } = await emitirYVer('planilla.pagada', {
        planillaId,
        fecha: fechaPago,
        formaPago,
        ...(formaPago === 'transferencia' ? { cuenta: cuentaEnCordobas(config, cuentaPago) } : {}),
      }));
    } catch (e) {
      motivo = e instanceof Error ? e.message : String(e);
    }
    onCerrar(motivo ? { planillaId, motivo } : undefined);
  }

  const segmento = (elegido: boolean) =>
    `min-w-0 flex-1 rounded-lg px-2 py-2 text-sm transition ${elegido ? 'bg-marca-600 font-medium text-white' : 'text-slate-400 hover:bg-white/5 hover:text-slate-200'}`;
  const periodo = rango.hasta.slice(0, 7);
  const segunda = !rango.desde.endsWith('-01');

  return (
    <Modal
      titulo={t(corrigiendo ? 'planilla.corregir.titulo' : 'planilla.correr')}
      onCerrar={onCerrar}
      pie={
        <div className="flex flex-col gap-2">
          {error && <p className="text-sm text-rose-400">{error}</p>}
          {bloqueada ? (
            <p className="text-sm text-amber-300" data-corregir-liquidados>
              {t('planilla.corregir.liquidados', { nombres: liquidados.join(', ') })}
            </p>
          ) : (
            <>
              {ilegibles.length > 0 && (
                <p className="text-xs text-amber-300" data-ilegibles>
                  {t('planilla.noSeEntiende', { nombres: ilegibles.join(', ') })}
                </p>
              )}
              {faltan.length > 0 && (
                <p className="text-xs text-amber-300" data-faltan-dias>
                  {t('planilla.faltanDias', { nombres: faltan.join(', ') })}
                </p>
              )}
              {corrigiendo && (
                <p className="tabular text-right text-xs text-slate-500" data-corregir-antes>
                  {t('planilla.corregir.antes', { neto: formatMoney(corrigiendo.totalNeto), costo: formatMoney(costoDeCorrida(corrigiendo)) })}
                </p>
              )}
              <div className="flex items-baseline justify-between text-sm">
                <span className="text-slate-400">
                  {t(yaSePago || corrigiendo?.pagada ? 'planilla.netoPagadoYCosto' : 'planilla.netoYCosto', { costo: formatMoney(previa.costo as Centavos) })}
                </span>
                <span className="tabular shrink-0 text-lg font-semibold whitespace-nowrap" data-corrida-neto>
                  {formatMoney(previa.neto as Centavos)}
                </span>
              </div>
              {previa.adelantos > 0 && (
                <p className="text-right text-xs text-amber-300">
                  {t('planilla.adelantosDescontados', {
                    monto: formatMoney(previa.adelantos as Centavos),
                    entregar: formatMoney((previa.neto - previa.adelantos) as Centavos),
                  })}
                </p>
              )}
              {anulando && cambio && (
                <p className="text-xs text-rose-300" data-corregir-anulando>
                  {t('planilla.corregir.anulando')}
                </p>
              )}
              {/* Paid, what was handed over changes with it: out of what paid it, or back into it, on the day it was paid. */}
              {corrigiendo?.pagada && delPago !== 0 && (
                <p className="text-right text-xs text-amber-300" data-corregir-pago>
                  {t(delPago > 0 ? 'planilla.corregir.pagoSale' : 'planilla.corregir.pagoVuelve', {
                    monto: formatMoney(Math.abs(delPago) as Centavos),
                    cuenta: pagadoCon,
                    fecha: corrigiendo.pagadaEn ?? '',
                  })}
                </p>
              )}
              {corrigiendo ? (
                <button
                  type="button"
                  className={anulando ? 'btn-peligro' : 'btn-primario'}
                  disabled={trabajando || !lista}
                  data-guardar-correccion
                  onClick={() => void guardarCorreccion()}
                >
                  <Ico icono={anulando ? 'anular' : 'confirmar'} />
                  {t(trabajando ? 'planilla.corregir.guardando' : !cambio ? 'planilla.corregir.sinCambios' : anulando ? 'planilla.corregir.anular' : 'planilla.corregir.guardar')}
                </button>
              ) : (
                <button type="button" className="btn-primario" disabled={trabajando || !lista} onClick={() => void correr()}>
                  <Ico icono="confirmar" />
                  {t(trabajando ? 'planilla.corriendo' : yaSePago ? 'planilla.registrarPagada' : 'planilla.cerrarCorrida')}
                </button>
              )}
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {!corrigiendo && enUso.length > 1 && (
          <div>
            <span className="etiqueta">{t('planilla.aQuienes')}</span>
            <div className="flex gap-1 rounded-xl bg-slate-800 p-1" role="group" aria-label={t('planilla.aQuienes')}>
              {enUso.map((f) => (
                <button
                  key={f}
                  type="button"
                  aria-pressed={frecuencia === f}
                  data-corrida-frecuencia={f}
                  data-sin-icono="pestana"
                  onClick={() => elegirFrecuencia(f)}
                  className={segmento(frecuencia === f)}
                >
                  {t(`planilla.frecuencia.${f}`)}
                </button>
              ))}
            </div>
          </div>
        )}

        {corrigiendo ? null : frecuencia === 'semanal' ? (
          <Campo
            etiqueta={t('planilla.semanaTermina')}
            type="date"
            value={rango.hasta}
            onChange={(ev) => ev.target.value && setRango(rangoDeSemana(ev.target.value))}
          />
        ) : (
          <Campo
            etiqueta={t('planilla.periodo')}
            type="month"
            value={periodo}
            onChange={(ev) => ev.target.value && setRango(frecuencia === 'quincenal' ? rangoDeQuincena(ev.target.value, segunda) : rangoDelMes(ev.target.value))}
            ayuda={frecuencia === 'mensual' ? t('planilla.ayudaPeriodo') : undefined}
          />
        )}
        {!corrigiendo && frecuencia === 'quincenal' && (
          <div className="flex gap-1 rounded-xl bg-slate-800 p-1" role="group" aria-label={t('planilla.queMitad')}>
            {[false, true].map((v) => (
              <button
                key={String(v)}
                type="button"
                aria-pressed={segunda === v}
                data-quincena={v ? 'segunda' : 'primera'}
                data-sin-icono="pestana"
                onClick={() => setRango(rangoDeQuincena(periodo, v))}
                className={segmento(segunda === v)}
              >
                {v ? t('planilla.segundaQuincena', { fin: Number(finDeMes(periodo).slice(8)) }) : t('planilla.primeraQuincena')}
              </button>
            ))}
          </div>
        )}
        {/* The run's name, whole: a sheet's title is cut to one line, and the days are what matters here. */}
        <div>
          <p className="font-semibold text-slate-100" data-corrida-titulo>
            {tituloDeCorrida(frecuencia, rango)}
          </p>
          {corrigiendo ? (
            <p className="mt-1 text-xs text-slate-500" data-corregir-estado>
              {corrigiendo.pagada ? t('planilla.corregir.pagada', { fecha: corrigiendo.pagadaEn ?? '', cuenta: pagadoCon }) : t('planilla.corregir.sinPagar')}
            </p>
          ) : (
            frecuencia !== 'mensual' && <p className="mt-1 text-xs text-slate-500">{t('planilla.ayudaPeriodo')}</p>
          )}
        </div>

        {corrigiendo && !bloqueada && (
          <Campo
            etiqueta={t('planilla.corregir.porQue')}
            value={nota}
            maxLength={300}
            onChange={(ev) => setNota(ev.target.value)}
            placeholder={t('planilla.corregir.porQueEjemplo')}
          />
        )}

        {/* Days already paid: recorded paid, the day it was and how. */}
        {!corrigiendo && (
          <div className="rounded-xl bg-slate-950/40 px-3 py-1 ring-1 ring-white/5 ring-inset" data-ya-pagada>
            <Interruptor etiqueta={t('planilla.yaSePago')} descripcion={t('planilla.yaSePagoAyuda')} checked={yaSePago} onChange={setYaSePago} />
            {yaSePago && (
              <div className="flex flex-col gap-3 pt-1 pb-3">
                <Campo
                  etiqueta={t('planilla.sePagoEl')}
                  type="date"
                  value={fechaPago}
                  min={rango.desde}
                  max={hoyEs}
                  onChange={(ev) => setFechaPagoEscrita(ev.target.value)}
                  error={fechaPagoBien ? null : t('planilla.fechaPagoFuera', { desde: rango.desde })}
                />
                <Selector etiqueta={t('compra.formaPago')} value={formaPago} onChange={(ev) => setFormaPago(ev.target.value as typeof formaPago)}>
                  <option value="efectivo_cordobas">{t('pago.efectivo_cordobas')}</option>
                  <option value="transferencia">{t('pago.transferencia')}</option>
                  <option value="billetera">{t('gastos.billeteraMovil')}</option>
                </Selector>
                {formaPago === 'transferencia' && (
                  <ElegirCuenta
                    valor={cuentaEnCordobas(config, cuentaPago)}
                    onCambiar={(c) => setCuentaPago(c as CuentaBancoCordobas)}
                    etiqueta={t('bancos.pagaDe')}
                  />
                )}
                {formaPago === 'efectivo_cordobas' && <p className="text-xs text-slate-500">{t('planilla.pagoDeEfectivoGeneral')}</p>}
              </div>
            )}
          </div>
        )}

        {!bloqueada && enGastos.length > 0 && (
          <p className="text-xs text-amber-300" data-sueldos-en-gastos>
            {t('planilla.sueldosEnGastos', {
              desde: rango.desde,
              lista:
                enGastos
                  .slice(0, 3)
                  .map((g) => `«${g.descripcion}» ${formatMoney(g.montoCordobas)} (${g.fecha})`)
                  .join(', ') + (enGastos.length > 3 ? ` ${t('planilla.yNMas', { n: enGastos.length - 3 })}` : ''),
            })}
          </p>
        )}

        {!bloqueada && gente.map((e) => {
          const b = borradores[e.id];
          if (!b) return null;
          const pago = pagoDe(e);
          const pagada = yaPagada(e);
          const incluido = b.incluir && !pagada;
          const l = lineas.find((x) => x.empleado.id === e.id)?.linea;
          const set = (campo: keyof Borrador, valor: string | boolean) =>
            setBorradores((xs) => ({
              ...xs,
              // A day worker's pay as their days were booked goes with the days (`borradorDeLinea`).
              [e.id]: { ...xs[e.id]!, [campo]: valor, ...(campo === 'dias' && pago.porDia ? { bruto: '' } : {}) },
            }));
          const tenia = suLinea.get(e.id);
          // What they have set aside to pay from: on a correction, what they had before the run.
          const estaba = comoEstaba(e);
          return (
            <div key={e.id} className="rounded-xl bg-slate-950/40 p-3 ring-1 ring-white/5 ring-inset" data-corrida-de={e.nombre}>
              <label className={`flex items-center gap-3 ${pagada ? '' : 'cursor-pointer'}`}>
                <input
                  type="checkbox"
                  checked={incluido}
                  disabled={Boolean(pagada)}
                  onChange={(ev) => set('incluir', ev.target.checked)}
                  className="size-5 accent-teal-500"
                />
                <span className="min-w-0 flex-1 truncate font-medium">{e.nombre}</span>
                <span className="tabular shrink-0 text-sm text-slate-400" data-corrida-bruto>
                  {l && !l.falta
                    ? formatMoney(l.bruto)
                    : pago.porDia && pago.tarifa !== null
                      ? dichoAcordado(pago.tarifa, 'dia')
                      : formatMoney(sueldoDeCorrida(e, frecuencia, rango))}
                </span>
              </label>
              {pagada && (
                <p className="mt-2 text-xs text-amber-300" data-ya-pagado>
                  {t('planilla.yaEsta', { planilla: tituloDe(pagada) })}
                </p>
              )}
              {/* What the run being corrected had for them, to hold the change against. */}
              {tenia && (
                <p className="tabular mt-1 text-xs text-slate-500" data-corregir-tenia>
                  {t('planilla.corregir.tenia', { bruto: formatMoney(tenia.brutoTotal), neto: formatMoney(tenia.neto) })}
                </p>
              )}
              {corrigiendo && !tenia && incluido && (
                <p className="mt-1 text-xs text-teal-300" data-corregir-nuevo>
                  {t('planilla.corregir.noEstaba')}
                </p>
              )}
              {incluido && pagaPrestaciones(estaba) && (() => {
                // Written down outside the run: their days are off what was earned already.
                const suyas = apuntadas.filter((v) => v.empleadoId === e.id && seCruzan(v, rango));
                if (suyas.length === 0) return null;
                return (
                  <p className="mt-2 text-xs text-amber-300" data-vacaciones-apuntadas>
                    {t('planilla.vacacionesApuntadas', {
                      dias: formatQuantity(suyas.reduce((a, v) => a + v.dias, 0) as Milli),
                      lista: suyas.map((v) => (v.desde === v.hasta ? v.desde : `${v.desde} – ${v.hasta}`)).join(', '),
                    })}
                  </p>
                );
              })()}

              {incluido && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {pago.porDia ? (
                    <Campo
                      etiqueta={t('planilla.diasTrabajados')}
                      inputMode="decimal"
                      className="tabular"
                      value={b.dias}
                      onChange={(ev) => set('dias', ev.target.value)}
                      placeholder="0"
                      ayuda={
                        l && !l.falta
                          ? t('planilla.diasPorTarifa', { tarifa: formatMoney((pago.tarifa ?? 0) as Centavos), monto: formatMoney(l.bruto) })
                          : t('planilla.ayudaDias', { tarifa: dichoAcordado((pago.tarifa ?? 0) as Centavos, 'dia') })
                      }
                    />
                  ) : (
                    <Campo
                      etiqueta={t(SALARIO_DE[frecuencia])}
                      inputMode="decimal"
                      className="tabular"
                      value={b.bruto}
                      onChange={(ev) => set('bruto', ev.target.value)}
                      placeholder={(sueldoDeCorrida(e, frecuencia, rango) / 100).toFixed(2)}
                      ayuda={
                        // A month's and a fortnight's days counted as thirty to the month; a week's, its own.
                        diasPagados(e, frecuencia, rango) !== null
                          ? t(frecuencia === 'semanal' ? 'planilla.desdeIngresoSemana' : 'planilla.desdeIngreso', {
                              fecha: e.fechaIngreso,
                              dias: diasPagados(e, frecuencia, rango)!,
                            })
                          : t('planilla.ayudaSalarioMes')
                      }
                    />
                  )}
                  <Campo
                    etiqueta={t('planilla.extrasGravables')}
                    inputMode="decimal"
                    className="tabular"
                    value={b.otrosGravables}
                    onChange={(ev) => set('otrosGravables', ev.target.value)}
                    placeholder="0.00"
                  />
                  {/* Vacations off for the shop (2026-10-31), and none set aside before: nothing to pay or rest. The
                      aguinaldo has a payment of its own, «Pagar aguinaldo» (2026-10-31). */}
                  {muestraVacaciones(estaba, config) && (
                    <>
                      <Campo
                        etiqueta={t('planilla.vacacionesPagadas')}
                        inputMode="decimal"
                        className="tabular"
                        value={b.vacacionesPagadas}
                        onChange={(ev) => set('vacacionesPagadas', ev.target.value)}
                        placeholder="0.00"
                        ayuda={t(pago.conInss ? 'planilla.tieneVacaciones' : 'planilla.tieneVacacionesSinInss', {
                          monto: formatMoney(estaba.vacacionesAcumuladas),
                        })}
                      />
                      <Campo
                        etiqueta={t('planilla.diasVacaciones')}
                        inputMode="decimal"
                        className="tabular"
                        value={b.diasVacaciones}
                        onChange={(ev) => set('diasVacaciones', ev.target.value)}
                        placeholder="0"
                        ayuda={t('planilla.diasAcumulados', { dias: (estaba.diasVacacionesAcumulados / 1000).toFixed(1) })}
                      />
                    </>
                  )}
                </div>
              )}
              {/* Paid with the run, from before it had a payment of its own: it stays as it was. */}
              {incluido && tenia && tenia.aguinaldoPagado > 0 && (
                <p className="mt-2 text-xs text-slate-500" data-corregir-aguinaldo>
                  {t('planilla.aguinaldo.deAntes', { monto: formatMoney(tenia.aguinaldoPagado) })}
                </p>
              )}
            </div>
          );
        })}

        {/* What it comes to, figure by figure: each person's, and what the run costs the business. */}
        {!bloqueada && listos.length > 0 && (
          <DesglosePlanilla
            personas={listos.map(({ empleado, linea }) => {
              // Typed, or a correction's as booked: the days since they started only when it is that.
              const escrito = (borradores[empleado.id]?.bruto ?? '').trim();
              return { empleado, libros: linea.libros, escrito: escrito !== '' && parseMoney(escrito) !== sueldoDeCorrida(empleado, frecuencia, rango) };
            })}
            frecuencia={frecuencia}
            rango={rango}
            config={config}
            pagada={yaSePago || Boolean(corrigiendo?.pagada)}
          />
        )}
      </div>
    </Modal>
  );
}
